import React, { useEffect, useMemo, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Color } from "../../../../../../../../components/Form/Button";
import { CheckboxGroup } from "../../../../../../../../components/Form/CheckboxGroup";
import Input from "../../../../../../../../components/Form/Input";
import RadioGroup from "../../../../../../../../components/Form/RadioGroup";
import CollapsablePanel from "../../../../../../../../components/Generic/Collapse";
import Modal from "../../../../../../../../components/Generic/Modal";
import { FLEET } from "../../../../../../../../constants";
import { useAppState } from "../../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../../hooks/useYupValidation";
import { JobType } from "../../../../../../../../models/jobType";
import { Measurement } from "../../../../../../../../models/measurement";
import { StorageBoxType } from "../../../../../../../../models/storage.box.type";
import { StorageBoxTypeSealRule } from "../../../../../../../../models/storage.box.type.seal.rule";
import {
  convertBasicUnits,
  CUBIC_METER_TO_CUBIC_FOOT_RATIO,
  handleGetConvertValue,
  handleSaveConvertValue,
  Metric,
} from "../../../../../../../../util/converts";
import { CreateRulesComponent } from "./components/CreateRulesComponent";
import { ListStorageBoxRulesComponent } from "./components/ListStorageBoxRules";
import useStyles, {
  ChecklistHeaderTitle,
  CounterBadge,
  MeasurementsItem,
  MeasurementsWrapper,
  MinimumInformation,
  RulesWrapper,
  StorageBoxRequireChecklistWrapper,
  StorageBoxRulesWrapper,
  StorageBoxUsageItem,
  StorageBoxUsagesWrapper,
} from "./styles";

function filterJobTypes(storageBoxRules: StorageBoxTypeSealRule[], jobTypes?: JobType[]) {
  const usedJobTypeIds = new Set(storageBoxRules?.map((rule) => rule.jobTypeId));

  return jobTypes?.filter((jobType) => !usedJobTypeIds.has(jobType.id));
}

interface Props {
  open: boolean;
  isFieldsDisabled: boolean;
  hasError?: boolean;
  isLoading: boolean;
  closeModal: () => void;
  storageBoxTypeToUpdate?: StorageBoxType;
  createStorageBoxType?: (storageBoxType: StorageBoxType) => void;
  updateStorageBoxType?: (storageBoxType: StorageBoxType, storageBoxTypeCapacity: number) => void;
  measurement?: Measurement;
  jobTypes?: JobType[];
  isLoadingJobTypes: boolean;
}

enum StorageBoxTypeMeasurements {
  WIDTH = "width",
  HEIGHT = "height",
  DEPTH = "depth",
}

function convertMilimiterToMeter(value: number) {
  return value / 1000;
}

export const StorageBoxTypesPageModal = ({
  open,
  isFieldsDisabled,
  hasError,
  isLoading,
  closeModal,
  createStorageBoxType,
  updateStorageBoxType,
  storageBoxTypeToUpdate,
  measurement,
  jobTypes,
  isLoadingJobTypes,
}: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();
  const { convertMetersToMillimeters, convertMillimetersToMeters } = convertBasicUnits();

  const [storageBoxTypeName, setStorageBoxTypeName] = useState<string>("");
  const [storageBoxTypeShareable, setStorageBoxTypeShareable] = useState<boolean>(true);
  const [isRequireChecklistStorageBoxType, isSetRequireChecklistStorageBoxType] =
    useState<boolean>(false);
  const [isStorageBoxUsageRoad, setIsStorageBoxUsageRoad] = useState<boolean>(false);
  const [isStorageBoxUsageWarehouse, setIsStorageBoxUsageWarehouse] = useState<boolean>(false);
  const [storageBoxTypeHeight, setStorageBoxTypeHeight] = useState<number | undefined>(0);
  const [storageBoxTypeWidth, setStorageBoxTypeWidth] = useState<number | undefined>(0);
  const [storageBoxTypeDepth, setStorageBoxTypeDepth] = useState<number | undefined>(0);
  const [storageBoxTypeCapacity, setStorageBoxTypeCapacity] = useState<number | undefined>(
    undefined
  );

  const [storageBoxTypeSealRules, setStorageBoxTypeSealRules] = useState<StorageBoxTypeSealRule[]>(
    []
  );
  const [rawStorageBoxTypeCapacity, setRawStorageBoxTypeCapacity] = useState<string>("");

  const storageBoxTypes = [isStorageBoxUsageRoad, isStorageBoxUsageWarehouse];

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  useEffect(() => {
    if (storageBoxTypeToUpdate) {
      const capacity = handleGetConvertValue(
        storageBoxTypeToUpdate?.capacity || 0,
        measurement?.code || "m³",
        measurement?.decimalPlaces
      );

      setStorageBoxTypeName(storageBoxTypeToUpdate?.name || "");
      setStorageBoxTypeShareable(
        storageBoxTypeToUpdate?.isShareable !== undefined
          ? storageBoxTypeToUpdate?.isShareable
          : true
      );
      setIsStorageBoxUsageRoad(storageBoxTypeToUpdate?.isStorageBoxUsageRoad || false);
      setIsStorageBoxUsageWarehouse(storageBoxTypeToUpdate?.isStorageBoxUsageWarehouse || false);
      setStorageBoxTypeHeight(convertMetersToMillimeters(storageBoxTypeToUpdate?.height));
      setStorageBoxTypeWidth(convertMetersToMillimeters(storageBoxTypeToUpdate?.width));
      setStorageBoxTypeDepth(convertMetersToMillimeters(storageBoxTypeToUpdate?.depth));
      setStorageBoxTypeCapacity(Number(capacity));
      setRawStorageBoxTypeCapacity(String(capacity));
      isSetRequireChecklistStorageBoxType(storageBoxTypeToUpdate.isRequireChecklist ?? false);
      setStorageBoxTypeSealRules(storageBoxTypeToUpdate?.storageBoxTypeSealRules || []);
    }
  }, []);

  const schema = yup.object().shape({
    storageBoxTypeName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test(
        "notAllowedTypeNameFleet",
        translate("theFleetNameIsUsedAsTheSystemDefault"),
        (name) => name?.toLowerCase()?.trim() !== FLEET
      ),
    storageBoxTypeCapacity: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("valueMustBeGreaterThanZero", translate("thisFieldMustBeGreaterThanZero"), (value) => {
        if (value && !!value && value > 0) {
          return true;
        }

        return false;
      }),
    storageBoxTypeRoad: yup.boolean(),
    storageBoxTypeWarehouse: yup.boolean(),
    storageBoxTypes: yup
      .array()
      .test("oneTypeIsRequired", translate("pleaseSelectAtLeastOne"), (values) => {
        if (values?.some((value) => value === true)) return true;

        return false;
      }),
    isRequireChecklistStorageBoxType: yup.boolean(),
    storageBoxTypeSealRules: yup
      .array()
      .of(
        yup.object().shape({
          jobTypeId: yup
            .string()
            .required(translate("thisFieldIsRequired"))
            .typeError(translate("thisFieldIsRequired")),
          sealsQuantity: yup
            .number()
            .min(1, translate("sealsValueMustBeGreaterThan0"))
            .required(translate("sealsValueMustBeGreaterThan0"))
            .typeError(translate("sealsValueMustBeGreaterThan0")),
        })
      )
      .nullable(),
  });

  const clearFields = () => {
    setStorageBoxTypeName("");
    setStorageBoxTypeHeight(0);
    setStorageBoxTypeWidth(0);
    setStorageBoxTypeDepth(0);
    setStorageBoxTypeCapacity(undefined);
  };

  const isValid = () => {
    try {
      schema.validateSync(storageBoxesFields, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);
    const capacityToSave = Number(
      handleSaveConvertValue(
        storageBoxTypeCapacity || 0,
        currentUser?.measurement?.code || currentUser?.company?.measurement?.code || "m³"
      )
    );

    const storageBoxTypeToSave: StorageBoxType = {
      ...storageBoxTypeToUpdate,
      name: storageBoxTypeName,
      width: convertMillimetersToMeters(storageBoxTypeWidth),
      height: convertMillimetersToMeters(storageBoxTypeHeight),
      depth: convertMillimetersToMeters(storageBoxTypeDepth),
      capacity: capacityToSave,
      isActive: true,
      isShareable: storageBoxTypeShareable,
      isStorageBoxUsageRoad,
      isStorageBoxUsageWarehouse,
      isRequireChecklist: isRequireChecklistStorageBoxType,
      storageBoxTypeSealRules,
    };

    if (isValid()) {
      if (storageBoxTypeToUpdate && storageBoxTypeToUpdate.id) {
        updateStorageBoxType?.(storageBoxTypeToSave, storageBoxTypeToUpdate.id);
      } else {
        createStorageBoxType?.(storageBoxTypeToSave);
      }
    }
  };

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  const storageBoxesFields = {
    storageBoxTypeName,
    storageBoxTypeCapacity,
    isStorageBoxUsageRoad,
    isStorageBoxUsageWarehouse,
    storageBoxTypes,
    isRequireChecklistStorageBoxType,
    storageBoxTypeSealRules,
  };

  useEffect(() => {
    if (hasFormBeenSubmitted) isValid();
  }, [hasFormBeenSubmitted]);

  const calculateStorageBoxCapacity = (width: number, height: number, depth: number) => {
    const calculatedCapacity =
      convertMillimetersToMeters(width) *
      convertMillimetersToMeters(height) *
      convertMillimetersToMeters(depth);

    const convertedCapacity = Number(
      handleGetConvertValue(
        calculatedCapacity || 0,
        measurement?.code || "m³",
        measurement?.decimalPlaces
      )
    );

    return convertedCapacity;
  };

  const handleUpdateStorageBoxTypeMeasurement = (
    measurement: StorageBoxTypeMeasurements,
    value: number
  ) => {
    switch (measurement) {
      case StorageBoxTypeMeasurements.WIDTH:
        setStorageBoxTypeWidth(value);
        if (storageBoxTypeHeight && storageBoxTypeDepth) {
          setStorageBoxTypeCapacity(
            calculateStorageBoxCapacity(value, storageBoxTypeHeight, storageBoxTypeDepth)
          );
        }
        break;
      case StorageBoxTypeMeasurements.HEIGHT:
        setStorageBoxTypeHeight(value);
        if (storageBoxTypeWidth && storageBoxTypeDepth) {
          setStorageBoxTypeCapacity(
            calculateStorageBoxCapacity(storageBoxTypeWidth, value, storageBoxTypeDepth)
          );
        }
        break;
      case StorageBoxTypeMeasurements.DEPTH:
        setStorageBoxTypeDepth(value);
        if (storageBoxTypeHeight && storageBoxTypeWidth) {
          setStorageBoxTypeCapacity(
            calculateStorageBoxCapacity(storageBoxTypeWidth, storageBoxTypeHeight, value)
          );
        }
        break;
      default:
        return null;
    }
  };

  const calculateMeasurementType = (measurementSum: number, measurementType?: string) => {
    if (measurementType) {
      switch (measurementType) {
        case Metric["ft³"]:
          return Number(measurementSum * CUBIC_METER_TO_CUBIC_FOOT_RATIO);

        case Metric["m³"]:
          return Number(measurementSum);
        default:
          return Number(measurementSum);
      }
    }

    return measurementSum;
  };

  useEffect(() => {
    if (
      storageBoxTypeHeight !== undefined &&
      storageBoxTypeWidth !== undefined &&
      storageBoxTypeDepth !== undefined
    ) {
      const storageBoxTypeHeightToMeters = convertMilimiterToMeter(storageBoxTypeHeight);
      const storageBoxTypeWidthToMeters = convertMilimiterToMeter(storageBoxTypeWidth);
      const storageBoxTypeDepthToMeters = convertMilimiterToMeter(storageBoxTypeDepth);

      const calcCapacityToMeters =
        storageBoxTypeHeightToMeters * storageBoxTypeWidthToMeters * storageBoxTypeDepthToMeters;

      const capacity = calculateMeasurementType(Number(calcCapacityToMeters), measurement?.code);

      if (capacity !== 0) {
        setStorageBoxTypeCapacity(Number(capacity));
        setRawStorageBoxTypeCapacity(Number(capacity).toFixed(measurement?.decimalPlaces));
      }
    }
  }, [storageBoxTypeHeight, storageBoxTypeWidth, storageBoxTypeDepth]);

  const capacityRegex = new RegExp(`^\\d*\\.?\\d{0,${measurement?.decimalPlaces}}$`);

  const filteredJobTypesOptions = filterJobTypes(storageBoxTypeSealRules, jobTypes);

  const getEmptyRule = useMemo(() => {
    return {
      jobType: filteredJobTypesOptions?.[0],
      jobTypeId: filteredJobTypesOptions?.[0]?.id,
      sealsQuantity: 0,
    } as StorageBoxTypeSealRule;
  }, [storageBoxTypeSealRules, jobTypes]);

  const handleNewCreateNewRule = () => {
    const newRule = getEmptyRule;

    setStorageBoxTypeSealRules((prev) => {
      const prevCopy = [...prev];

      prevCopy.push(newRule);

      return prevCopy;
    });
  };

  const renderStorageBoxRules = () => {
    if (storageBoxTypeSealRules?.length) {
      return (
        <ListStorageBoxRulesComponent
          disabled={isFieldsDisabled}
          jobTypes={jobTypes}
          storageBoxTypeSealRules={storageBoxTypeSealRules}
          onChange={(attribute: string, index: number, value: JobType | number) => {
            updateStorageBoxRule(attribute, index, value);
          }}
          handleAddRule={handleNewCreateNewRule}
          isLoadingJobTypes={isLoadingJobTypes}
          errors={errors}
          handleRemoveStorageBoxTypeRule={(id: number) => handleRemoveStorageBoxTypeSealRule(id)}
        />
      );
    }

    return (
      <CreateRulesComponent
        handleCreateRule={handleNewCreateNewRule}
        isLoadingJobTypes={isLoadingJobTypes}
      />
    );
  };

  const updateStorageBoxRule = (attribute: string, index: number, value: JobType | number) => {
    setStorageBoxTypeSealRules((prev) => {
      const prevCopy = [...prev];

      if (attribute === "jobType") {
        const newJobType = value as JobType;

        prevCopy[index] = {
          ...storageBoxTypeSealRules[index],
          jobType: newJobType,
          jobTypeId: newJobType.id,
        };
      } else {
        prevCopy[index] = { ...storageBoxTypeSealRules[index], [attribute]: value };
      }

      return prevCopy;
    });
  };

  const handleRemoveStorageBoxTypeSealRule = (id: number) => {
    const newStorageBoxTypeSealRules = storageBoxTypeSealRules.filter(
      (item) => item.jobTypeId !== id
    );

    setStorageBoxTypeSealRules(newStorageBoxTypeSealRules);
  };

  const renderChecklistHeaderTitle = () => {
    const storageBoxTypeSealRulesLength = storageBoxTypeSealRules?.length;

    return (
      <ChecklistHeaderTitle>
        {translate("checklistRules")}
        <CounterBadge>{storageBoxTypeSealRulesLength}</CounterBadge>
      </ChecklistHeaderTitle>
    );
  };

  return (
    <Modal
      open={open}
      handleClose={() => {
        clearFields();
        closeModal();
      }}
      title={
        storageBoxTypeToUpdate ? translate("editStorageBoxType") : translate("newStorageBoxType")
      }
      disableEnforceFocus
      data-cy="newStorageBoxType"
      dataCyTitle="newStorageBoxTypeTitle"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        customColorCancel: Color.TEXT_INFO,
        dataCySave: "submitChangesButton",
        textSave: storageBoxTypeToUpdate ? translate("save") : translate("create"),
        loading: isLoading,
        saveDisabled: isLoading,
        onSave: () => handleSubmit(),
      }}
      className={classes.modalStyles}
    >
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        <Grid item xs={12}>
          <Input
            data-cy="storageBoxTypeName"
            required
            placeholder={translate("name")}
            label={translate("name")}
            error={fieldHasError("storageBoxTypeName")}
            errorMessage={getErrorByField("storageBoxTypeName")}
            value={storageBoxTypeName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setStorageBoxTypeName(event.target.value)
            }
            disabled={isFieldsDisabled}
          />
        </Grid>
        <MeasurementsWrapper>
          <MeasurementsItem>
            <Input
              data-cy="storageBoxTypeHeight"
              placeholder={"-"}
              label={`${translate("height")} (mm)`}
              error={fieldHasError("storageBoxTypeHeight")}
              errorMessage={getErrorByField("storageBoxTypeHeight")}
              value={storageBoxTypeHeight}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const valueNumber = Number(event.target.value.replace(/[^0-9]/g, ""));

                handleUpdateStorageBoxTypeMeasurement(
                  StorageBoxTypeMeasurements.HEIGHT,
                  valueNumber
                );
              }}
              disabled={isFieldsDisabled}
            />
            <MinimumInformation>{`${translate("minimumValue")} 1`}</MinimumInformation>
          </MeasurementsItem>
          <MeasurementsItem>
            <Input
              data-cy="storageBoxTypeWidth"
              placeholder={"-"}
              label={`${translate("width")} (mm)`}
              error={fieldHasError("storageBoxTypeWidth")}
              errorMessage={getErrorByField("storageBoxTypeWidth")}
              value={storageBoxTypeWidth}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const valueNumber = Number(event.target.value.replace(/[^0-9]/g, ""));

                handleUpdateStorageBoxTypeMeasurement(
                  StorageBoxTypeMeasurements.WIDTH,
                  valueNumber
                );
              }}
              disabled={isFieldsDisabled}
            />
            <MinimumInformation>{`${translate("minimumValue")} 1`}</MinimumInformation>
          </MeasurementsItem>
          <MeasurementsItem>
            <Input
              data-cy="storageBoxTypeDepth"
              placeholder={"-"}
              label={`${translate("depth")} (mm)`}
              error={fieldHasError("storageBoxTypeDepth")}
              errorMessage={getErrorByField("storageBoxTypeDepth")}
              value={storageBoxTypeDepth}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const valueNumber = Number(event.target.value.replace(/[^0-9]/g, ""));

                handleUpdateStorageBoxTypeMeasurement(
                  StorageBoxTypeMeasurements.DEPTH,
                  valueNumber
                );
              }}
              disabled={isFieldsDisabled}
            />
            <MinimumInformation>{`${translate("minimumValue")} 1`}</MinimumInformation>
          </MeasurementsItem>
          <MeasurementsItem>
            <Input
              data-cy="storageBoxTypeCapacity"
              required
              placeholder={"-"}
              label={`${translate("capacity")} (${measurement?.code})`}
              error={fieldHasError("storageBoxTypeCapacity")}
              errorMessage={getErrorByField("storageBoxTypeCapacity")}
              value={rawStorageBoxTypeCapacity}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (capacityRegex.test(event.target.value)) {
                  setRawStorageBoxTypeCapacity(event.target.value);
                  setStorageBoxTypeCapacity(Number(event.target.value));
                }
              }}
              disabled={isFieldsDisabled}
            />
          </MeasurementsItem>
        </MeasurementsWrapper>
        <StorageBoxUsagesWrapper>
          <StorageBoxUsageItem>
            <RadioGroup
              required
              data-cy="storageBoxTypeShareable"
              label={translate("shareable")}
              description={translate("defineWheterTheStorageBoxTypeCanApperInAllWarehouses")}
              value={storageBoxTypeShareable}
              className={classes.radioGroupStyles}
              onChange={(event) =>
                event?.target?.value === "true"
                  ? setStorageBoxTypeShareable(true)
                  : setStorageBoxTypeShareable(false)
              }
              options={[
                {
                  label: translate("yes"),
                  classes: classes.optionsLabel,
                  value: true,
                  "data-cy": "storageBoxTypeShareableYes",
                },
                {
                  label: translate("no"),
                  classes: classes.optionsLabel,
                  value: false,
                  "data-cy": "storageBoxTypeShareableNo",
                },
              ]}
              disabled={isFieldsDisabled}
            />
          </StorageBoxUsageItem>
          <StorageBoxUsageItem>
            <CheckboxGroup
              label={translate("usage")}
              description={translate(
                "defineIfTheStorageBoxTypeCanBeUsedInTransitInsideTheWarehouseOrBoth"
              )}
              disabled={isFieldsDisabled}
              required
              options={[
                {
                  label: translate("road"),
                  value: isStorageBoxUsageRoad,
                  "data-cy": "storageBoxTypeRoad",
                  onChange() {
                    setIsStorageBoxUsageRoad(!isStorageBoxUsageRoad);
                  },
                  hasError: fieldHasError("storageBoxTypes"),
                  disabled: isFieldsDisabled,
                },
                {
                  label: translate("warehouse"),
                  value: isStorageBoxUsageWarehouse,
                  "data-cy": "storageBoxTypeWarehouse",
                  onChange() {
                    setIsStorageBoxUsageWarehouse(!isStorageBoxUsageWarehouse);
                  },
                  hasError: fieldHasError("storageBoxTypes"),
                  disabled: isFieldsDisabled,
                },
              ]}
              error={fieldHasError("storageBoxTypes")}
              errorMessage={getErrorByField("storageBoxTypes")}
              className={classes.checkboxGroup}
            />
          </StorageBoxUsageItem>
        </StorageBoxUsagesWrapper>
        <StorageBoxRequireChecklistWrapper item xs={12}>
          <StorageBoxUsageItem>
            <RadioGroup
              required
              data-cy="storageBoxTypeRequireCheckList"
              label={translate("checklist")}
              description={translate("defineWheterTheStorageBoxTypeRequiresPhotosSealNumberOrBoth")}
              value={isRequireChecklistStorageBoxType}
              className={classes.radioGroupStyles}
              onChange={(event) =>
                event?.target?.value === "true"
                  ? isSetRequireChecklistStorageBoxType(true)
                  : isSetRequireChecklistStorageBoxType(false)
              }
              options={[
                {
                  label: translate("yes"),
                  classes: classes.optionsLabel,
                  value: true,
                  "data-cy": "storageBoxTypeRequireChecklistYes",
                },
                {
                  label: translate("no"),
                  classes: classes.optionsLabel,
                  value: false,
                  "data-cy": "storageBoxTypeRequireChecklistNo",
                },
              ]}
              disabled={isFieldsDisabled}
            />
          </StorageBoxUsageItem>
        </StorageBoxRequireChecklistWrapper>
        {!!isRequireChecklistStorageBoxType && (
          <StorageBoxRulesWrapper item xs={12}>
            <CollapsablePanel
              title={renderChecklistHeaderTitle()}
              subtitle={translate(
                "ifNoRuleIsAddedToAnOrderTypeTheRequiredSealNumberWillBeEqualTo1"
              )}
              panelClassName={classes.panel}
              panelContentClassName={classes.panelContent}
              collapsed={!storageBoxTypeSealRules?.length}
            >
              <RulesWrapper>{renderStorageBoxRules()}</RulesWrapper>
            </CollapsablePanel>
          </StorageBoxRulesWrapper>
        )}
      </Grid>
    </Modal>
  );
};
